import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import {
  Target,
  BarChart3,
  MessagesSquare,
  FolderHeart,
  UserRound,
  Presentation,
} from "lucide-react";

// Fotos von Unsplash, lokal ausgeliefert (keine Verbindung zu Dritten)
const IMG_ATHLETE = `${process.env.PUBLIC_URL}/assets/photos/sportler.jpg`;

const Card = ({ children, className = "", testId }) => (
  <motion.div
    data-testid={testId}
    whileHover={{ y: -6 }}
    transition={{ type: "spring", stiffness: 260, damping: 24 }}
    className={`group relative rounded-3xl border border-white/10 backdrop-blur-xl overflow-hidden ${className}`}
  >
    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
      <div className="absolute -inset-px rounded-3xl bg-gradient-to-br from-brand-orange/15 to-brand-blue/15 blur-md" />
    </div>
    <div className="relative h-full">{children}</div>
  </motion.div>
);

const Icon = ({ as: Component, tone = "orange" }) => (
  <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-white/[0.06] border border-white/10 mb-5">
    <Component
      className={`w-5 h-5 ${
        tone === "orange" ? "text-brand-orange" : "text-brand-blue"
      }`}
    />
  </span>
);

export const BentoFeatures = () => {
  return (
    <section
      id="plattform"
      data-testid="features-section"
      className="relative py-24 lg:py-32 border-t border-white/[0.06]"
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-brand-blue/10 blur-[160px]" />
      </div>

      <div className="px-6 md:px-10 lg:px-16">
        <Reveal className="max-w-2xl mb-14">
          <p className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-brand-orange mb-5">
            Die Plattform
          </p>
          <h2 className="font-display font-bold tracking-tight text-4xl md:text-5xl lg:text-6xl leading-[0.98]">
            Alles zwischen Verein und Sponsor{" "}
            <span className="gradient-text">an einem Ort</span>.
          </h2>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-white/55">
            Sechs Bausteine, die ineinandergreifen - vom ersten Profil bis zur
            Auswertung nach der Saison.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6 auto-rows-[minmax(180px,auto)]">
          {/* Matching - große Karte mit Bild */}
          <Card testId="feature-card-matching" className="md:col-span-7 md:row-span-2">
            <div className="absolute inset-0">
              <img
                src={IMG_ATHLETE}
                alt="Athlet"
                className="w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/70 to-navy-950/20" />
            </div>
            <div className="relative h-full flex flex-col justify-end p-8 md:p-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-white/[0.06] border border-white/10">
                  <Target className="w-5 h-5 text-brand-orange" />
                </span>
                <span className="text-xs uppercase tracking-[0.2em] font-bold text-white/50">
                  Matching
                </span>
              </div>
              <h3 className="font-display font-bold text-3xl md:text-4xl text-white max-w-md">
                Der richtige Sponsor findet euch.
              </h3>
              <p className="mt-4 max-w-md text-base leading-relaxed text-white/60">
                MatchGate gleicht die Angaben beider Seiten ab - Region,
                Sportart, Zielgruppe, Reichweite, Themen - und drückt die
                Übereinstimmung als MatchScore aus. Statt hundert Möglichkeiten
                stehen drei sinnvolle Gespräche.
              </p>
            </div>
          </Card>

          {/* Analytics */}
          <Card testId="feature-card-analytics" className="md:col-span-5 p-8">
            <Icon as={BarChart3} tone="blue" />
            <h3 className="font-display font-bold text-2xl text-white mb-2">
              Wirkung, die sich belegen lässt
            </h3>
            <p className="text-base leading-relaxed text-white/55">
              Jedes Sponsoring bekommt beim Abschluss ein eigenes Dashboard. Es
              belegt über die ganze Saison, was vereinbart war: mit Fotos, Scans
              und Klicks.
            </p>
            <div className="mt-6 flex items-end gap-1.5 h-14">
              {[40, 65, 50, 80, 60, 95, 72].map((h, i) => (
                <div
                  key={i}
                  style={{ height: `${h}%` }}
                  className="flex-1 rounded-t bg-gradient-to-t from-brand-orange/40 to-brand-blue/70"
                />
              ))}
            </div>
          </Card>

          {/* Chat */}
          <Card testId="feature-card-chat" className="md:col-span-5 p-8">
            <Icon as={MessagesSquare} />
            <h3 className="font-display font-bold text-2xl text-white mb-2">
              Erstkontakt ohne Umweg
            </h3>
            <p className="text-base leading-relaxed text-white/55">
              Der integrierte Chat führt direkt zur zuständigen Person, statt in
              einem Sammelpostfach zu landen.
            </p>
          </Card>

          {/* Projekte */}
          <Card testId="feature-card-projects" className="md:col-span-6 lg:col-span-4 p-8">
            <Icon as={FolderHeart} tone="blue" />
            <h3 className="font-display font-bold text-2xl text-white mb-2">
              Projekte statt Bittbriefe
            </h3>
            <p className="text-base leading-relaxed text-white/55">
              Vereine können einzelne Vorhaben ausschreiben, mit Ziel, Budget
              und Gegenleistung.
            </p>
          </Card>

          {/* Profile & Suche */}
          <Card testId="feature-card-profiles" className="md:col-span-6 lg:col-span-4 p-8">
            <Icon as={UserRound} />
            <h3 className="font-display font-bold text-2xl text-white mb-2">
              Profile, die vergleichbar sind
            </h3>
            <p className="text-base leading-relaxed text-white/55">
              Alle Angaben in derselben Struktur - und eine Filtersuche über
              Vereine, Projekte und Unternehmen.
            </p>
          </Card>

          {/* Vereins-Werkzeuge */}
          <Card testId="feature-card-tools" className="md:col-span-12 lg:col-span-4 p-8">
            <Icon as={Presentation} tone="blue" />
            <h3 className="font-display font-bold text-2xl text-white mb-2">
              Werkzeuge für Vereine
            </h3>
            <p className="text-base leading-relaxed text-white/55">
              Pitch-Generator für Sponsorenunterlagen, Media-Kit-Templates für
              den Auftritt in den eigenen Kanälen.
            </p>
          </Card>
        </div>
      </div>
    </section>
  );
};
